import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Unlock, ArrowRight, Wine, Users, Shield, Clock, Droplets, QrCode, Building2 } from 'lucide-react';

const PIN = '091764';

const T = {
  bg: '#0D0C0B', bgEl: '#1A1918', border: '#2A2826',
  gold: '#C5A059', goldDim: '#8A7342',
  text: '#F9F6F0', textSec: '#A39F98',
  font: { h: "'Playfair Display', serif", b: "'DM Sans', sans-serif" },
};

const SECTIONS = [
  {
    id: 'guiado',
    label: 'Camarero Automático',
    desc: 'Flujo guiado conversacional',
    path: '/mesa/5/guiado',
    icon: Wine,
    accent: '#C5A059',
  },
  {
    id: 'menu',
    label: 'Menú Digital',
    desc: 'Vista cliente (Mesa 5)',
    path: '/mesa/5',
    icon: Users,
    accent: '#D4AF37',
  },
  {
    id: 'admin',
    label: 'Panel Admin',
    desc: 'Gestión completa',
    path: '/admin',
    icon: Shield,
    accent: '#8B7355',
  },
  {
    id: 'fichar',
    label: 'Fichaje',
    desc: 'Reloj empleados',
    path: '/fichar',
    icon: Clock,
    accent: '#7A8B6F',
  },
  {
    id: 'water',
    label: 'Love & Thanks Water',
    desc: 'Landing del agua',
    path: '/landing',
    icon: Droplets,
    accent: '#5B8FA8',
  },
  {
    id: 'qr',
    label: 'QR Experience',
    desc: 'Simbolismo botella',
    path: '/lovewater',
    icon: QrCode,
    accent: '#8B6FA8',
  },
  {
    id: 'b2b',
    label: 'B2B Landing',
    desc: 'Página comercial SaaS',
    path: '/b2b',
    icon: Building2,
    accent: '#A88B6F',
  },
];

const SystemPanel = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = sessionStorage.getItem('system_auth');
    if (stored === 'true') setAuthenticated(true);
  }, []);

  useEffect(() => {
    if (!authenticated && inputRef.current) inputRef.current.focus();
  }, [authenticated]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin === PIN) {
      sessionStorage.setItem('system_auth', 'true');
      setAuthenticated(true);
      setError(false);
    } else {
      setError(true);
      setShake(true);
      setPin('');
      setTimeout(() => setShake(false), 500);
    }
  };

  const handlePinChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(val);
    setError(false);
  };

  /* ── PIN Screen ── */
  if (!authenticated) {
    return (
      <div style={{
        minHeight: '100dvh', background: T.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '24px',
      }}>
        <style>{`
          @keyframes shakeX {
            0%, 100% { transform: translateX(0); }
            20%, 60% { transform: translateX(-8px); }
            40%, 80% { transform: translateX(8px); }
          }
        `}</style>

        <form onSubmit={handleSubmit} data-testid="system-pin-form" style={{
          width: '100%', maxWidth: '320px', textAlign: 'center',
          animation: shake ? 'shakeX 0.4s ease' : 'none',
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 24px',
            background: T.bgEl, border: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Lock size={28} color={T.gold} />
          </div>

          <h1 style={{
            fontFamily: T.font.h, fontSize: '24px', fontWeight: 500,
            color: T.text, marginBottom: '8px',
          }}>
            Pont 10 System
          </h1>
          <p style={{
            fontFamily: T.font.b, fontSize: '14px', color: T.textSec, marginBottom: '32px',
          }}>
            Acceso restringido
          </p>

          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={handlePinChange}
              placeholder="PIN"
              data-testid="system-pin-input"
              style={{
                width: '100%', padding: '16px 20px', borderRadius: '14px',
                border: `1px solid ${error ? '#DC5050' : T.border}`,
                background: T.bgEl, color: T.text, fontSize: '20px',
                fontFamily: T.font.b, textAlign: 'center', letterSpacing: '0.3em',
                outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
            />
          </div>

          {error && (
            <p data-testid="system-pin-error" style={{
              fontFamily: T.font.b, fontSize: '13px', color: '#DC5050', marginBottom: '12px',
            }}>
              PIN incorrecto
            </p>
          )}

          <button type="submit" data-testid="system-pin-submit" style={{
            width: '100%', padding: '16px', borderRadius: '999px', border: 'none',
            background: pin.length === 6 ? T.gold : T.border,
            color: pin.length === 6 ? T.bg : T.textSec,
            fontFamily: T.font.b, fontSize: '16px', fontWeight: 600,
            cursor: pin.length === 6 ? 'pointer' : 'default',
            transition: 'all 0.2s', letterSpacing: '0.01em',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            <Unlock size={18} /> Acceder
          </button>
        </form>
      </div>
    );
  }

  /* ── Dashboard ── */
  return (
    <div style={{
      minHeight: '100dvh', background: T.bg, padding: '24px',
    }}>
      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', paddingTop: '32px', marginBottom: '40px' }}>
          <p style={{
            fontFamily: T.font.b, fontSize: '11px', fontWeight: 600,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: T.gold, marginBottom: '12px',
          }}>
            Pont 10 &middot; System
          </p>
          <h1 style={{
            fontFamily: T.font.h, fontSize: '28px', fontWeight: 500,
            color: T.text, lineHeight: 1.3, marginBottom: '8px',
          }}>
            Panel de desarrollo
          </h1>
          <p style={{
            fontFamily: T.font.b, fontSize: '14px', color: T.textSec,
          }}>
            Accesos directos a todas las secciones
          </p>
        </div>

        {/* Section cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => navigate(section.path)}
                data-testid={`system-link-${section.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '16px',
                  padding: '18px 20px', borderRadius: '16px',
                  background: T.bgEl, border: `1px solid ${T.border}`,
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                  background: `${section.accent}15`, border: `1px solid ${section.accent}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={22} color={section.accent} strokeWidth={1.5} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontFamily: T.font.b, fontSize: '16px', fontWeight: 600,
                    color: T.text, margin: 0,
                  }}>
                    {section.label}
                  </p>
                  <p style={{
                    fontFamily: T.font.b, fontSize: '13px', color: T.textSec,
                    margin: '2px 0 0',
                  }}>
                    {section.desc}
                  </p>
                </div>
                <ArrowRight size={18} color={T.textSec} />
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <p style={{
          textAlign: 'center', fontFamily: T.font.b, fontSize: '12px',
          color: `${T.textSec}66`, marginTop: '40px', paddingBottom: '24px',
        }}>
          Sesión activa en esta pestaña
        </p>
      </div>
    </div>
  );
};

export default SystemPanel;
